import { readWebMDuration } from './WebMDuration';

export interface FileResolution {
  width: number;
  height: number;
  duration: number;
}

export class FileSource {
  private objectUrl: string | null = null;
  /**
   * If set, a parsed-from-the-container duration that should override
   * `<video>.duration`. Needed for Safari WebM playback where the native
   * duration reading is unreliable. We apply this via `Object.defineProperty`
   * on the video element for the lifetime of this source.
   */
  private endedFired = false;
  private manualDuration: number | null = null;
  private originalDurationDescriptor: PropertyDescriptor | undefined;

  constructor(private readonly video: HTMLVideoElement) {}

  async start(file: File): Promise<FileResolution> {
    this.stop();

    // Parse the container's own duration *before* loading the video.
    // Safari 18.x reports `<video>.duration === Infinity` for WebM from
    // webm-muxer despite the container carrying a valid SegmentInfo
    // Duration, which cascades into a broken seek bar / timer / ended
    // event / "Open viewer" affordance. We use the parsed value as the
    // ground truth and override `video.duration` on the element.
    let parsedDurationSec: number | null = null;
    try {
      parsedDurationSec = await readWebMDuration(file);
    } catch (err) {
      console.warn('[FileSource] EBML duration parse failed', err);
    }
    console.info(
      '[FileSource] parsedDurationSec=',
      parsedDurationSec,
      'fileType=',
      file.type,
      'fileSize=',
      file.size,
    );

    this.objectUrl = URL.createObjectURL(file);
    this.video.srcObject = null;
    this.video.src = this.objectUrl;
    this.video.loop = false;
    this.video.muted = true;
    this.video.playsInline = true;
    await this.waitForMetadata();
    console.info(
      '[FileSource] after loadedmetadata video.duration=',
      this.video.duration,
      'readyState=',
      this.video.readyState,
    );

    let duration = this.video.duration;
    const nativeDurationBad = !Number.isFinite(duration) || duration <= 0;

    // Even when the native video.duration looks OK, prefer the parsed
    // value when available — Safari has been observed reporting a
    // finite-but-wrong duration for some WebM streams. Always install
    // the override so the UI uses our ground truth.
    if (parsedDurationSec !== null && parsedDurationSec > 0) {
      duration = parsedDurationSec;
      this.installDurationOverride(parsedDurationSec);
    } else if (nativeDurationBad) {
      duration = await this.probeDurationBySeeking();
      if (Number.isFinite(duration) && duration > 0) {
        this.installDurationOverride(duration);
      }
    }
    console.info('[FileSource] final duration=', duration);

    this.video.play().then(
      () => {
        console.info(
          '[FileSource] play() resolved — paused=',
          this.video.paused,
          'currentTime=',
          this.video.currentTime,
        );
      },
      (err) => {
        console.warn(
          '[FileSource] play() rejected:',
          (err && err.name) || err,
          (err && err.message) || '',
        );
      },
    );
    // Surface state transitions so we can see if the video actually plays.
    const logState =
      (label: string): (() => void) =>
      () => {
        console.info(
          '[FileSource:' + label + '] paused=',
          this.video.paused,
          't=',
          this.video.currentTime.toFixed(3),
          'ready=',
          this.video.readyState,
        );
      };
    this.video.addEventListener('play', logState('play'));
    this.video.addEventListener('playing', logState('playing'));
    this.video.addEventListener('pause', logState('pause'));
    this.video.addEventListener('stalled', logState('stalled'));
    this.video.addEventListener('waiting', logState('waiting'));
    this.video.addEventListener('ended', logState('ended'));
    let tuCount = 0;
    this.video.addEventListener('timeupdate', () => {
      tuCount += 1;
      if (tuCount <= 3 || tuCount % 15 === 0) {
        console.info(
          '[FileSource:timeupdate#' + tuCount + '] t=',
          this.video.currentTime.toFixed(3),
        );
      }
    });
    return {
      width: this.video.videoWidth,
      height: this.video.videoHeight,
      duration: Number.isFinite(duration) && duration > 0 ? duration : 0,
    };
  }

  /**
   * Shadow the prototype `duration` getter on the video instance so
   * reads return our parsed duration. The browser's internal playback
   * logic still uses its own duration (which may be Infinity), so we
   * also synthesise `ended` + `timeupdate` behaviour.
   */
  private installDurationOverride(durationSec: number): void {
    this.manualDuration = durationSec;
    this.originalDurationDescriptor =
      Object.getOwnPropertyDescriptor(this.video, 'duration') ?? undefined;
    try {
      Object.defineProperty(this.video, 'duration', {
        configurable: true,
        get: () => durationSec,
      });
      console.info(
        '[FileSource] installed duration override =',
        durationSec,
        'readback=',
        this.video.duration,
      );
    } catch (err) {
      console.warn('[FileSource] duration override failed', err);
    }
    // Clear any prior listener; attach fresh.
    this.endedFired = false;
    this.video.addEventListener('timeupdate', this.fireSyntheticEnded);
    this.video.addEventListener('seeked', this.onSeekedResetEnded);
    // Also trigger `durationchange` so consumers (the HUD) recompute.
    try {
      this.video.dispatchEvent(new Event('durationchange'));
    } catch {
      /* ignore */
    }
  }

  private fireSyntheticEnded = (): void => {
    if (this.manualDuration === null) return;
    if (this.endedFired) return;
    if (this.video.currentTime + 0.05 >= this.manualDuration) {
      this.endedFired = true;
      try {
        this.video.pause();
      } catch {
        /* ignore */
      }
      try {
        this.video.dispatchEvent(new Event('ended'));
      } catch {
        /* ignore */
      }
    }
  };

  private onSeekedResetEnded = (): void => {
    if (this.manualDuration === null) return;
    if (this.video.currentTime + 0.05 < this.manualDuration) {
      this.endedFired = false;
    }
  };

  private probeDurationBySeeking(): Promise<number> {
    const video = this.video;
    return new Promise((resolve) => {
      let settled = false;
      const finish = (value: number): void => {
        if (settled) return;
        settled = true;
        video.removeEventListener('seeked', onSeeked);
        video.removeEventListener('timeupdate', onTimeUpdate);
        try {
          video.currentTime = 0;
        } catch {
          /* ignore */
        }
        resolve(value);
      };
      const onSeeked = (): void => {
        const t = video.currentTime;
        if (Number.isFinite(t) && t > 0) finish(t);
      };
      const onTimeUpdate = (): void => {
        const t = video.currentTime;
        if (Number.isFinite(t) && t > 0) finish(t);
      };
      video.addEventListener('seeked', onSeeked);
      video.addEventListener('timeupdate', onTimeUpdate);
      try {
        video.currentTime = 1e7; // way past any realistic duration
      } catch {
        finish(Number.NaN);
        return;
      }
      setTimeout(() => {
        const t = video.currentTime;
        finish(Number.isFinite(t) && t > 0 ? t : Number.NaN);
      }, 3000);
    });
  }

  stop(): void {
    this.video.pause();
    this.video.removeAttribute('src');
    this.video.load();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }
    // Uninstall our duration override / synthetic ended listeners.
    this.video.removeEventListener('timeupdate', this.fireSyntheticEnded);
    this.video.removeEventListener('seeked', this.onSeekedResetEnded);
    if (this.manualDuration !== null) {
      try {
        if (this.originalDurationDescriptor) {
          Object.defineProperty(this.video, 'duration', this.originalDurationDescriptor);
        } else {
          // Delete the instance property so the prototype getter comes back.
          delete (this.video as unknown as { duration?: unknown }).duration;
        }
      } catch {
        /* ignore */
      }
    }
    this.manualDuration = null;
    this.originalDurationDescriptor = undefined;
    this.endedFired = false;
  }

  private waitForMetadata(): Promise<void> {
    return new Promise((resolve) => {
      if (this.video.readyState >= 1) {
        resolve();
        return;
      }
      this.video.addEventListener('loadedmetadata', () => resolve(), {
        once: true,
      });
    });
  }
}
